import { AnimatePresence, motion } from "motion/react"

import { STAGE_SPRING } from "@/components/coach-stage"
import { PageContainer } from "@/components/page-container"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { greetingFor } from "@/lib/greeting"
import { initialsOf } from "@nl/shared"

import {
  ComposerSlot,
  ProgressPanel,
  RecentList,
  SpeakButton,
  SuggestionChips,
  TileGrid,
  moduleRows,
  type StartLayoutProps,
} from "./shared"

/**
 * Draft "Coach-Karte": one large card in front of the hero, the coach with
 * portrait and headline like an assistant, suggestion chips under the input,
 * modules large in two columns.
 */
export function CoachCardLayout(p: StartLayoutProps) {
  const rows = moduleRows(p.recommendations)
  return (
    <>
      <section className="relative w-full overflow-hidden bg-[var(--surface-alt)]">
        <img
          src="/fotos/hero-elbphilharmonie.jpg"
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover object-right dark:opacity-60"
        />
        <div
          aria-hidden
          className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent_0%,transparent_60%,var(--paper)_100%)]"
        />
        <div className="relative z-10 mx-auto flex min-h-[560px] w-full max-w-[1100px] items-center justify-start px-8 py-16">
          <motion.div
            layoutId="coach-card"
            transition={STAGE_SPRING}
            className="w-full max-w-[720px] rounded-xl bg-[var(--paper)]/78 p-7 shadow-[0_24px_80px_rgba(31,31,31,0.18)] ring-1 ring-[var(--paper)]/80 backdrop-blur-2xl sm:p-8"
          >
            <AnimatePresence mode="wait" initial={false}>
              {p.voice ? (
                <motion.div
                  key="voice"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="-m-7 sm:-m-8 [&>div:last-child]:rounded-b-xl"
                >
                  {p.voice}
                </motion.div>
              ) : (
                <motion.div
                  key="prompt"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col gap-6 sm:flex-row"
                >
                  <Avatar className="size-24 shrink-0 rounded-2xl ring-1 ring-[var(--rule)]">
                    {p.coach?.portraitUrl ? (
                      <AvatarImage src={p.coach.portraitUrl} alt="" className="rounded-2xl" />
                    ) : null}
                    <AvatarFallback className="rounded-2xl bg-accent text-[24px] font-medium text-primary">
                      {p.coach ? initialsOf(p.coach.name) : ""}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="font-display text-[18px] font-medium tracking-tight">
                        {p.coachName}
                      </span>
                      <span className="text-[13px] text-muted-foreground">
                        {p.coach?.headline || "Dein KI-Coach"}
                      </span>
                    </div>
                    <h1 className="mt-3 font-display text-[28px] leading-[1.12] font-medium tracking-tight sm:text-[32px]">
                      {greetingFor(new Date())}
                      {p.firstName ? `, ${p.firstName}` : ""}.{" "}
                      <span className="text-foreground/65">Woran arbeiten wir heute?</span>
                    </h1>
                    {p.standHint ? (
                      <p className="mt-2 text-[13.5px] text-muted-foreground">{p.standHint}</p>
                    ) : null}
                    <div className="mt-5 flex flex-col gap-2.5 sm:flex-row sm:items-stretch">
                      <ComposerSlot>{p.composer}</ComposerSlot>
                      <SpeakButton coachName={p.coachName} onSpeak={p.onSpeak} tone="filled" />
                    </div>
                    <SuggestionChips className="mt-4" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <PageContainer className="mt-10 pb-14">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="min-w-0 space-y-9">
            {rows.map((row) => (
              <TileGrid key={row.title} row={row} shape="large" cols={2} />
            ))}
          </div>
          <aside className="space-y-8 lg:pt-11">
            <ProgressPanel
              resume={p.resume}
              completedCount={p.completedCount}
              accent="red"
            />
            <RecentList
              entries={p.entries}
              isLoading={p.entriesLoading}
              coachName={p.coachName}
            />
          </aside>
        </div>
      </PageContainer>
    </>
  )
}
